import CurrenciesDAO       from '@/domains/Currencies/CurrenciesDAO';
import BaseService         from '@/core/services/BaseService';
import Core                from "@/core/Core";
import Currency            from '@/domains/Currencies/Currency';
import DateSimple          from '@/core/types/DateSimple';
import type TimeStamp from "@/core/types/TimeStamp";
import CurrencyRate        from '@/core/types/CurrencyRate';
import CurrencyRatesCache  from './CurrencyRatesCache';

/**
 * Сервис для работы с валютами и их курсами
 */
class CurrenciesService extends BaseService {
	private static isInitialized = false;
	private static cache = CurrencyRatesCache;

	/**
	 * Карта всех доступных валют.
	 * Дефолты — литералы `as Currency`, а НЕ через Currency.fromServer: карта
	 * собирается в статик-инициализаторе на этапе загрузки модуля, когда Core
	 * ещё не готов, а fromServer → BaseEntity.fromServer обращается к Core
	 * (циклическая зависимость, ReferenceError 'Core' before initialization).
	 * Настоящие валюты приходят позже в loadCurrencies() уже через fromServer.
	 */
	static currencies = reactive(new Map<string, Currency>([
		['RUB', { id: 1, code: 'RUB', symbol: 'P',   description: 'Российский рубль' }   as Currency],
		['USD', { id: 2, code: 'USD', symbol: '$',   description: 'Доллар США' }         as Currency],
		['EUR', { id: 3, code: 'EUR', symbol: '€',   description: 'Евро' }               as Currency],
		['UZS', { id: 4, code: 'UZS', symbol: 'сўм', description: 'Узбекский сум' }      as Currency],
		['CNY', { id: 5, code: 'CNY', symbol: '¥',   description: 'Китайский юань' }     as Currency],
		['INR', { id: 6, code: 'INR', symbol: '₹',   description: 'Индийская рупия'  }   as Currency],
		['CHF', { id: 7, code: 'CHF', symbol: '₣',   description: 'Швейцарский франк' }  as Currency],
		['GBP', { id: 8, code: 'GBP', symbol: '£',   description: 'Фунт стерлингов' }    as Currency]
	]));

	/**
	 * Валюта, в которой пользователь просит показывать все конвертируемые
	 * величины. Хранится на пользователе, переключается из шапки.
	 *
	 * null — пользователя нет: момент до логина, а также выход, когда
	 * компоненты успевают перерисоваться до сброса состояния (см. комментарий
	 * в AclService.logout) и код валюты уже пуст.
	 */
	static get displayCurrency(): Currency | null {
		const code = Core.displayedCurrencyCode;

		if (code === '') {
			return null;
		} else {
			return this.currencies.get(code) ?? null;
		}
	}

	/**
	 * Курс валюты к локальной валюте партнёра из кеша, без обращения к серверу.
	 * Синхронный близнец getRate: нужен отображению, где promise недопустим —
	 * Vue не делает реактивную обвязку вокруг промисов.
	 *
	 * @param currencyCode - Код валюты, курс которой нужен
	 * @param date - Дата курса
	 *
	 * @returns Курс валюты или null, если курса в кеше нет
	 */
	static getRateCached(currencyCode: string, date: Date | DateSimple): CurrencyRate | null {
		// Курс локальной валюты к себе всегда равен 1
		if (currencyCode === Core.acl.currentPartnerCurrencyCode) {
			return CurrencyRate.fromServer('1');
		} else {
			return this.cache.get(this.toDateSimple(date), currencyCode);
		}
	}

	/**
	 * Курса по этой паре не существует: запрос отработал, курс не пришёл.
	 *
	 * Отличает «ждать бесполезно» от «ещё едет». Курсы хранятся не за все
	 * календарные дни и не за всю историю по всем валютам, переноса курса на
	 * беке нет — пустой ответ штатен и постоянен, и показывать его надо не так,
	 * как незавершённую загрузку.
	 *
	 * @param currencyCode - Код валюты
	 * @param date - Дата курса
	 */
	static isRateMissing(currencyCode: string, date: Date | DateSimple): boolean {
		const dateObj = this.toDateSimple(date);

		return this.cache.isRequested(dateObj, currencyCode) && this.cache.get(dateObj, currencyCode) === null;
	}

	/**
	 * Инициализирует сервис валют
	 */
	static async init() {
		if (this.isInitialized) {
			Core.error('CurrenciesService уже инициализирован');
			return this;
		}
		try {
			// инициализируем только если пользователь залогинен, поскольку нам нужно знать для какой страны получать данные
			Core.eventBus.AclLogin.addEventListener(() => this.loadCurrencies());
			Core.eventBus.AclSessionRestored.addEventListener(() => this.loadCurrencies());
			// и очищаем кеш если пользователь выходит, он может зайти под другим партнером
			Core.eventBus.AclSessionRestored.addEventListener(() => this.cache.clearAll());
			Core.eventBus.AclLogout.addEventListener(() => this.cache.clearAll());

			this.isInitialized = true;
			//Core.debug('💵 CurrenciesService инициализирован');
		} catch (error) {
			Core.error('Ошибка инициализации CurrenciesService:', error);
		}
	}

	/**
	 * Получает курс одной валюты к другой на указанную дату с кешированием
	 *
	 * @param currencyCodeFrom - Код валюты-источника (например, 'USD')
	 * @param currencyCodeTo - Код целевой валюты (например, 'RUB')
	 * @param date - Дата для получения курса
	 *
	 * @returns Курс валюты (число с плавающей точкой)
	 *
	 * @throws {Error} Если не удалось получить курс с сервера
	 *
	 * @example
	 * const rate = await getRate('USD', 'RUB', new Date());
	 * console.log(rate); // 84.39
	 *
	 * @example
	 * // Курс локальной валюты к себе всегда равен 1
	 * const localRate = await getRate('RUB', 'RUB', new Date());
	 * console.log(localRate); // 1
	 */
	static async getRate(
		currencyCodeFrom: string,
		currencyCodeTo: string,
		date: Date | string
	): Promise<CurrencyRate> {
		// Проверка не хочет ли пользователь получить курс на локальную валюту
		if (currencyCodeFrom === Core.acl.currentPartnerCurrencyCode) return CurrencyRate.fromServer('1');

		const dateObj = date instanceof DateSimple ? date : new DateSimple(date);
		const cachedRate = this.cache.get(dateObj, currencyCodeFrom);

		if (cachedRate !== null) return cachedRate;


		// Загружаем и кешируем
		const response = await CurrenciesDAO.getSingleRate(
			currencyCodeFrom,
			currencyCodeTo,
			dateObj,
			Core.currentPartner!.country.code
		);
		const rate = CurrencyRate.fromServer(response.data);

		this.cache.set(dateObj, currencyCodeFrom, rate);
		return rate;
	}

	/**
	 * Получает курсы для набора пар «валюта + дата» одним запросом
	 *
	 * Что берётся откуда:
	 *   - курс валюты к себе — 1, на сервер не идёт
	 *   - что есть в кеше — из кеша
	 *   - остальное — одним запросом, дубли пар отсекаются до отправки
	 *
	 * Пары, для которых курса нет (выходные, отсутствующая история, неизвестная
	 * валюта), в результате просто отсутствуют — вызывающий код обязан это учитывать.
	 *
	 * @param currencyCodeTo - Код целевой валюты (к которой приводятся курсы)
	 * @param pairs - Список пар «валюта + дата»
	 *
	 * @returns Курсы, сгруппированные по дате: { '2026-07-24': { USD: CurrencyRate } }
	 *
	 * @example
	 * const rates = await getRates('RUB', [
	 *     { currencyCodeFrom: 'USD', date: '2026-07-24' },
	 *     { currencyCodeFrom: 'EUR', date: '2026-07-24' }
	 * ]);
	 */
	static async getRates(
		currencyCodeTo: string,
		pairs: { currencyCodeFrom: string, date: Date | string }[]
	): Promise<Record<string, Record<string, CurrencyRate>>> {
		const result: Record<string, Record<string, CurrencyRate>> = {};
		const requestedPairs: { currencyCodeFrom: string, date: DateSimple }[] = [];
		const requestedKeys = new Set<string>();

		pairs.forEach(pair => {
			let dateObj: DateSimple;
			if (pair.date instanceof DateSimple) {
				dateObj = pair.date;
			}
			else {
				dateObj = new DateSimple(pair.date);
			}
			const dateKey = dateObj.toDateString();

			if (pair.currencyCodeFrom === currencyCodeTo) {
				// Курс валюты к себе всегда равен 1
				this.setRateToResult(result, dateKey, pair.currencyCodeFrom, CurrencyRate.fromServer('1'));
			}
			else {
				const cachedRate = this.cache.get(dateObj, pair.currencyCodeFrom);
				if (cachedRate !== null) {
					this.setRateToResult(result, dateKey, pair.currencyCodeFrom, cachedRate);
				}
				else {
					// Дубли пар отсекаем до запроса — бек их переживёт, но гонять незачем
					const pairKey = dateKey + '|' + pair.currencyCodeFrom;
					if (!requestedKeys.has(pairKey)) {
						requestedKeys.add(pairKey);
						requestedPairs.push({ currencyCodeFrom: pair.currencyCodeFrom, date: dateObj });
					}
				}
			}
		});

		if (requestedPairs.length === 0) return result;

		const response = await CurrenciesDAO.getRates(currencyCodeTo, requestedPairs);

		if (!response.ok) return result;

		// Гидрируем строковые значения курсов в CurrencyRate и кешируем
		Object.keys(response.data).forEach(dateKey => {
			const ratesForDate = response.data[dateKey];
			const dateObj = new DateSimple(dateKey);

			Object.keys(ratesForDate).forEach(currencyCode => {
				const rate = CurrencyRate.fromServer(ratesForDate[currencyCode]);

				this.setRateToResult(result, dateKey, currencyCode, rate);
				this.cache.set(dateObj, currencyCode, rate);
			});
		});

		/** Отмечаем ВСЕ отправленные пары, а не только вернувшиеся. Отметка
		 *  нужна именно там, где курса не оказалось: без неё отображение не
		 *  отличит «курса нет» от «ещё едет», а повторные вызовы будут снова
		 *  и снова ходить на сервер за тем, чего там нет. */
		requestedPairs.forEach(pair => {
			this.cache.markRequested(pair.date, pair.currencyCodeFrom);
		});

		return result;
	}

	/**
	 * Кладёт курс в результат getRates, создавая группу даты при необходимости.
	 * Группа создаётся только под реальный курс — пустых объектов в результате нет.
	 */
	private static setRateToResult(
		result: Record<string, Record<string, CurrencyRate>>,
		dateKey: string,
		currencyCode: string,
		rate: CurrencyRate
	): void {
		if (!result[dateKey]) {
			result[dateKey] = {};
		}

		result[dateKey][currencyCode] = rate;
	}

	/**
	 * Пары «валюта + дата», по которым отображение спросило курс и не нашло.
	 * Копится синхронно за проход отрисовки, улетает одним вызовом getRates.
	 *
	 * Намеренно НЕ reactive: запись идёт во время рендера, а реактивная запись
	 * в этот момент — источник трудноуловимых повторных пересчётов. В кеш
	 * (который реактивен) пишем уже после ответа сервера.
	 */
	private static pendingRates = new Map<string, { currencyCode: string, date: DateSimple }>();

	/** Пары, по которым запрос отправлен и ответа ещё нет. Без этого набора
	 *  отрисовка поверх незавершённой загрузки поставит те же пары в очередь
	 *  повторно и уйдёт второй запрос за тем же самым. */
	private static loadingRates = new Set<string>();

	/** Отправка очереди уже запланирована на конец текущего прохода. */
	private static isFlushScheduled = false;

	/**
	 * Регистрирует потребность в курсе. Зовётся отображением при промахе кеша.
	 *
	 * Ничего не ждёт и ничего не возвращает: курс появится в кеше позже, кеш
	 * реактивен, Vue пересчитает выражение сам. Пары копятся за проход
	 * отрисовки и уходят одним запросом — иначе грид на сотню строк породил бы
	 * сотню запросов.
	 *
	 * Благодаря этому механизму отображению не нужна предзагрузка: курсы
	 * доберутся сами, ценой одной лишней перерисовки. Явный вызов getRates
	 * до отрисовки остаётся возможным — он просто убирает эту перерисовку.
	 *
	 * @param currencyCode - Код валюты
	 * @param date - Дата курса
	 */
	static requestRate(currencyCode: string, date: Date | DateSimple): void {
		const dateObj = this.toDateSimple(date);
		const pairKey = dateObj.toDateString() + '|' + currencyCode;

		if (currencyCode === Core.acl.currentPartnerCurrencyCode) {
			// Курс локальной валюты к себе не запрашивается, он всегда 1
		}
		else if (this.cache.get(dateObj, currencyCode) !== null) {
			// Курс уже в кеше
		}
		else if (this.cache.isRequested(dateObj, currencyCode)) {
			// Уже спрашивали, курса нет — повторять бессмысленно
		}
		else if (this.loadingRates.has(pairKey)) {
			// Запрос по этой паре в полёте
		}
		else {
			this.pendingRates.set(pairKey, { currencyCode, date: dateObj });

			if (this.isFlushScheduled) {
				// Отправка уже запланирована
			}
			else {
				this.isFlushScheduled = true;
				/** setTimeout, а не nextTick: микрозадача может выполниться между
				 *  компонентами и разрезать один грид на несколько запросов. */
				setTimeout(() => this.flushPendingRates(), 0);
			}
		}
	}

	/**
	 * Отправляет накопленную очередь одним вызовом getRates.
	 * Пары помечаются «в полёте» на время запроса и снимаются в finally: при
	 * сетевом сбое отметка «спрашивали» не ставится, и следующая отрисовка
	 * попробует снова — иначе разрыв связи навсегда превратился бы в «курса нет».
	 */
	private static async flushPendingRates(): Promise<void> {
		const pairs = [...this.pendingRates.values()];

		this.pendingRates.clear();
		this.isFlushScheduled = false;

		if (pairs.length === 0) {
			// Очередь опустела, пока ждали таймер
			return;
		}

		const pairKeys = pairs.map(pair => pair.date.toDateString() + '|' + pair.currencyCode);

		pairKeys.forEach(pairKey => this.loadingRates.add(pairKey));

		try {
			await this.getRates(
				Core.acl.currentPartnerCurrencyCode,
				pairs.map(pair => ({ currencyCodeFrom: pair.currencyCode, date: pair.date }))
			);
		}
		finally {
			pairKeys.forEach(pairKey => this.loadingRates.delete(pairKey));
		}
	}

  private static toDateSimple(date: Date | DateSimple | TimeStamp | string): DateSimple {
    if (DateSimple.isDateSimple(date)) {
      return date;
    } else {
      return new DateSimple(date);
    }
  }

	/**
	 * Очищает кеш курсов валют
	 * @param date - Конкретная дата для очистки (если не указана, очищается весь кеш)
	 *
	 * @example
	 * // Очистить весь кеш
	 * CurrenciesService.clearRatesCache();
	 *
	 * // Очистить кеш для конкретной даты
	 * CurrenciesService.clearRatesCache(new Date('2024-08-29'));
	 */
	static clearRatesCache(date?: Date | string): void {
		this.cache.clear(date);
	}

	/**
	 * Загружает список валют и их курсы с сервера
	 * Обновляет внутренние карты currencies и кеш
	 */
	private static async loadCurrencies(): Promise<void> {
		const response = await CurrenciesDAO.getList(
			// фолбек для не залогиненого пользователя
			Core.currentPartner.country.code || Core.config.localCountry.code,
			Core.currentPartner.country.localCurrency.code || Core.config.localCurrency.code,
		);

		if (!response.ok) return;

		response.data.forEach((record: any) => {
			const currency = Currency.fromServer(record);
			// Обновляем список валют
			this.currencies.set(currency.code, currency);
			// Кешируем курс за сегодня, если он есть
			if (currency.rate && !currency.rate.isEmpty() && currency.rateDate) {
				this.cache.set(currency.rateDate, currency.code, currency.rate);
			}
		});

		// Core.debug('💵 CurrenciesService валюты скачены');
	}
}

export default CurrenciesService;
